import { Card, CardBody, CardHeader, CardTitle } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, MapPin } from "lucide-react";
import type { JSX } from "react";
import { Link, useParams } from "react-router-dom";

import { apiRequest } from "../../common/api/apiClient.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import { ServiceTypeBadge, serviceTypeClass } from "../../common/components/ServiceTypeBadge.js";
import type { components } from "../../generated/api.js";
import { AppointmentPicker } from "./AppointmentPicker.js";
import { EventBooking } from "./EventBooking.js";
import { MemberBenefitNote } from "./MemberBenefitNote.js";
import { RentBooking } from "./RentBooking.js";
import { RouteBooking } from "./RouteBooking.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];

/**
 * Die Seite eines Angebots: was es ist, wo, was es kostet — und darunter die Buchung,
 * die zur Buchungsart passt. Eine Seite für alle Arten (AGENTS.md: ein Ablauf); nur
 * der Wähler darunter wechselt.
 */
export function OfferPage(): JSX.Element {
  const { id = "" } = useParams();
  const offer = useQuery({
    queryKey: ["offer", id],
    queryFn: () => apiRequest<ServiceDTO>(`/offers/${encodeURIComponent(id)}`, { tenant: null }),
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-6">
      <Link
        to="/"
        className="-ml-2 mt-2 inline-flex min-h-[44px] items-center gap-1.5 px-2 text-[13.5px] font-bold text-ink-2"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Entdecken
      </Link>

      {offer.isPending && <p className="mt-4 text-[14px] text-ink-3">Wird geladen …</p>}
      {offer.isError && (
        <p className="mt-4 text-[14px] text-danger">Dieses Angebot gibt es nicht (mehr).</p>
      )}

      {offer.data && (
        <div className={`${serviceTypeClass(offer.data.type)} mt-2 grid gap-4`}>
          <div className="flex items-start gap-3">
            <ServiceTypeBadge type={offer.data.type} square />
            <div className="min-w-0 flex-1">
              <h1 className="text-[22px] font-extrabold tracking-[-0.03em] text-ink">
                {offer.data.title}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <ServiceTypeBadge type={offer.data.type} />
                {offer.data.startsAt && (
                  <span className="num inline-flex items-center gap-1 text-[13px] font-bold text-ink-2">
                    <CalendarDays size={13} aria-hidden="true" />
                    {formatWhen(offer.data.startsAt, offer.data.venueTimeZone)}
                  </span>
                )}
                {offer.data.venueName && (
                  <span className="inline-flex items-center gap-1 text-[13px] text-ink-2">
                    <MapPin size={13} aria-hidden="true" />
                    {offer.data.venueName}
                  </span>
                )}
              </div>
            </div>
            {/* Bei einer Fahrt hängt der Preis an der Teilstrecke; den nennt die Buchung. */}
            {offer.data.stopCount === 0 && (
              <span className="text-[17px] font-extrabold text-ink">
                <Money cents={offer.data.priceCents} currency={offer.data.currency} />
              </span>
            )}
          </div>

          {offer.data.description && (
            <p className="text-[14px] leading-relaxed text-ink-2">{offer.data.description}</p>
          )}

          <MemberBenefitNote serviceId={offer.data.id} />

          <Card>
            <CardHeader>
              <CardTitle>Buchen</CardTitle>
            </CardHeader>
            <BookingSection offer={offer.data} />
          </Card>
        </div>
      )}
    </div>
  );
}

/** Wählt den Buchungsweg nach Art — ein Ablauf, drei Wähler (ADR 0006). */
function BookingSection({ offer }: { offer: ServiceDTO }): JSX.Element {
  if (offer.type === "APPOINTMENT" && offer.startsAt === null) {
    return (
      <CardBody>
        <AppointmentPicker offer={offer} />
      </CardBody>
    );
  }
  if (offer.stopCount > 0) {
    return (
      <CardBody>
        <RouteBooking offer={offer} />
      </CardBody>
    );
  }
  if (offer.type !== "RENT") {
    // Ein fester Termin bucht sich wie ein Event mit Kontingent 1.
    return (
      <CardBody>
        <EventBooking offer={offer} />
      </CardBody>
    );
  }
  return (
    <CardBody>
      <RentBooking offer={offer} />
    </CardBody>
  );
}
